import { Injectable } from '@angular/core';
import { Control, type StoreControl } from 'commons';
import { db } from '../../../app/db';
import { ConfidenceBandSeries } from '../classes/ConfidenceBand.class';

/**
 * Service for building confidence band series for chart visualization.
 * Confidence bands represent Min/Max bounds around a main series.
 */
@Injectable({ providedIn: 'root' })
export class ConfidenceBandBuilderService {
  /**
   * Applies confidence band series to the chart
   */
  async applyConfidenceBands(controls: StoreControl[], laneIndices: number[], controlIndices: number[], existingSeries: unknown[]): Promise<void> {
    const resolvedSeries = await Promise.all(existingSeries);

    const confidenceBandPromises = laneIndices.flatMap((laneIndex) =>
      controlIndices.flatMap((controlIndex) =>
        controls.map((storeControl) => this.buildConfidenceBandSeries(storeControl, laneIndex, controlIndex, resolvedSeries))
      )
    );

    const confidenceBands = await Promise.all(confidenceBandPromises);
    const flattenedBands = confidenceBands.flat().filter(Boolean);
    existingSeries.push(...flattenedBands);
  }

  /**
   * Builds confidence band series for a single control
   */
  private async buildConfidenceBandSeries(storeControl: StoreControl, laneIndex: number, controlIndex: number, resolvedSeries: unknown[]): Promise<unknown[]> {
    const control = new Control(storeControl);

    if (!control.confidenceBand) {
      return [];
    }

    const seriesId = storeControl.id + laneIndex + controlIndex;
    const matchingSeries = resolvedSeries.find((s: any) => s.id === seriesId);

    if (!matchingSeries) {
      return [];
    }

    const telemetryValues = (await db.channelData.get(control.mappingString))?.timeseries;

    if (!telemetryValues) {
      return [];
    }

    return [
      new ConfidenceBandSeries({
        control,
        series: matchingSeries,
        telemetryValues,
        direction: 'Min',
      }).config,
      new ConfidenceBandSeries({
        control,
        series: matchingSeries,
        telemetryValues,
        direction: 'Max',
      }).config,
    ];
  }
}
